<template lang="pug">
.hierarchical-industry-select.relative
  //- Instructional text
  p.text-sm.text-gray-500.dark_text-gray-400.mb-1.mt-0(v-if="instructionalText")
    | {{ instructionalText }}
  
  //- Main dropdown trigger
  .dropdown-trigger.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded.cursor-pointer.flex.justify-between.items-center(
    @click="toggleDropdown"
    :class="{ 'border-blue-500': isOpen }"
  )
    span.text-gray-900.dark_text-gray-100 {{ selectedIndustryName || 'Select industry' }}
    .flex.items-center
      Icon.icon.text-gray-400.ml-2(
        :icon="isOpen ? 'teenyicons:up-small-outline' : 'teenyicons:down-small-outline'"
        class="w-4 h-4"
      )
  
  //- Dropdown content
  .dropdown-content.absolute.top-full.left-0.right-0.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded.shadow-lg.z-50.max-h-96.overflow-y-auto(
    v-if="isOpen"
    @click.stop
  )
    .p-2
      //- Render hierarchical tree
      .industry-tree
        .industry-item(
          v-for="(node, nodeId) in industryTree" 
          :key="nodeId"
        )
          .industry-node.flex.items-center.p-2.rounded.cursor-pointer.hover_bg-gray-100.dark_hover_bg-gray-600(
            @click="selectIndustry(node)"
            :class="{ 'bg-blue-100 dark_bg-blue-900': selectedIndustryId === node.id }"
          )
            //- Expand/collapse icon for nodes with children
            .expand-icon.mr-2.w-6.h-6.flex.items-center.justify-center.cursor-pointer.hover_bg-gray-200.dark_hover_bg-gray-600.rounded(
              v-if="hasChildren(node)"
              @click.stop="toggleNode(nodeId)"
            )
              Icon.icon.text-gray-600.dark_text-gray-300(
                :icon="expandedNodes.has(nodeId) ? 'teenyicons:down-small-outline' : 'teenyicons:right-small-outline'"
                class="w-4 h-4"
              )
            //- Placeholder for consistent spacing when no children
            .w-6.mr-2(v-else)
            
            //- Industry name
            span.text-gray-900.dark_text-gray-100 {{ node.name }}
          
          //- Render children if expanded
          .children.ml-4(
            v-if="expandedNodes.has(nodeId) && hasChildren(node)"
          )
            .industry-item(
              v-for="(childNode, childId) in node.children" 
              :key="childId"
            )
              .industry-node.flex.items-center.p-2.rounded.cursor-pointer.hover_bg-gray-100.dark_hover_bg-gray-600(
                @click="selectIndustry(childNode)"
                :class="{ 'bg-blue-100 dark_bg-blue-900': selectedIndustryId === childNode.id }"
                            )
                //- Expand/collapse icon for child nodes with children
                .expand-icon.mr-2.w-6.h-6.flex.items-center.justify-center.cursor-pointer.hover_bg-gray-200.dark_hover_bg-gray-600.rounded(
                  v-if="hasChildren(childNode)"
                  @click.stop="toggleNode(childId)"
                )
                  Icon.icon.text-gray-600.dark_text-gray-300(
                    :icon="expandedNodes.has(childId) ? 'teenyicons:down-small-outline' : 'teenyicons:right-small-outline'"
                    class="w-4 h-4"
                  )
                //- Placeholder for consistent spacing when no children
                .w-6.mr-2(v-else)
                
                //- Child industry name
                span.text-gray-900.dark_text-gray-100 {{ childNode.name }}
              
              //- Render grandchildren if expanded
              .children.ml-4(
                v-if="expandedNodes.has(childId) && hasChildren(childNode)"
              )
                .industry-item(
                  v-for="(grandChildNode, grandChildId) in childNode.children" 
                  :key="grandChildId"
                )
                  .industry-node.flex.items-center.p-2.rounded.cursor-pointer.hover_bg-gray-100.dark_hover_bg-gray-600(
                    @click="selectIndustry(grandChildNode)"
                    :class="{ 'bg-blue-100 dark_bg-blue-900': selectedIndustryId === grandChildNode.id }"
                  )
                    //- Expand/collapse icon for grandchild nodes with children (Level 3)
                    .expand-icon.mr-2.w-6.h-6.flex.items-center.justify-center.cursor-pointer.hover_bg-gray-200.dark_hover_bg-gray-600.rounded(
                      v-if="hasChildren(grandChildNode)"
                      @click.stop="toggleNode(grandChildId)"
                    )
                      Icon.icon.text-gray-600.dark_text-gray-300(
                        :icon="expandedNodes.has(grandChildId) ? 'teenyicons:down-small-outline' : 'teenyicons:right-small-outline'"
                        class="w-4 h-4"
                      )
                    //- Placeholder for consistent spacing when no children
                    .w-6.mr-2(v-else)
                    
                    //- Grandchild industry name
                    span.text-gray-900.dark_text-gray-100 {{ grandChildNode.name }}
                  
                  //- Render great-grandchildren if expanded (Level 4)
                  .children.ml-4(
                    v-if="expandedNodes.has(grandChildId) && hasChildren(grandChildNode)"
                  )
                    .industry-item(
                      v-for="(greatGrandChildNode, greatGrandChildId) in grandChildNode.children" 
                      :key="greatGrandChildId"
                    )
                      .industry-node.flex.items-center.p-2.rounded.cursor-pointer.hover_bg-gray-100.dark_hover_bg-gray-600(
                        @click="selectIndustry(greatGrandChildNode)"
                        :class="{ 'bg-blue-100 dark_bg-blue-900': selectedIndustryId === greatGrandChildNode.id }"
                      )
                        //- No expand icon for Level 4 (final level)
                        .w-6.mr-2
                        
                        //- Great-grandchild industry name (Level 4)
                        span.text-gray-900.dark_text-gray-100 {{ greatGrandChildNode.name }}

  //- Click outside to close
  .fixed.inset-0.z-40(v-if="isOpen" @click="closeDropdown")
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { Icon } from '@iconify/vue';

interface IndustryNode {
  id: string;
  name: string;
  level: number;
  children?: Record<string, IndustryNode>;
}

interface Props {
  modelValue?: string; // The selected industry ID
  industries: Record<string, IndustryNode>;
  placeholder?: string;
  instructionalText?: string;
}

interface Emits {
  (e: 'update:modelValue', value: string): void;
  (e: 'change', value: { id: string; name: string }): void;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Select industry',
  instructionalText: ''
});

const emit = defineEmits<Emits>();

// Component state
const isOpen = ref(false);
const expandedNodes = ref(new Set<string>());
const selectedIndustryId = ref(props.modelValue || '');
const selectedIndustryName = ref('');

// Computed properties
const industryTree = computed(() => {
  return filterOutOtherNodes(props.industries);
});

// Recursively filter out "Other" nodes from all levels
const filterOutOtherNodes = (
  industries: Record<string, IndustryNode>
): Record<string, IndustryNode> => {
  const filterNode = (node: IndustryNode): IndustryNode => {
    const filteredNode = { ...node };

    if (node.children) {
      filteredNode.children = {};
      for (const [childId, childNode] of Object.entries(node.children)) {
        // Filter out nodes containing "other" at any level
        if (childNode.name.toLowerCase().includes('other')) {
          continue; // Skip this node
        }
        filteredNode.children[childId] = filterNode(childNode);
      }
    }

    return filteredNode;
  };

  const filtered: Record<string, IndustryNode> = {};
  for (const [id, node] of Object.entries(industries)) {
    filtered[id] = filterNode(node);
  }

  return filtered;
};

// Methods
const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

const closeDropdown = () => {
  isOpen.value = false;
};

const toggleNode = (nodeId: string) => {
  if (expandedNodes.value.has(nodeId)) {
    expandedNodes.value.delete(nodeId);
  } else {
    expandedNodes.value.add(nodeId);
  }
};

const selectIndustry = (node: IndustryNode) => {
  selectedIndustryId.value = node.id;
  selectedIndustryName.value = node.name;

  // Emit the change
  emit('update:modelValue', node.id);
  emit('change', { id: node.id, name: node.name });

  // Close dropdown after selection
  closeDropdown();
};

// Helper function to recursively find industry name by ID
const findIndustryNameById = (
  industries: Record<string, IndustryNode>,
  targetId: string
): string => {
  for (const [id, node] of Object.entries(industries)) {
    if (id === targetId) return node.name;
    if (node.children) {
      const found = findIndustryNameById(node.children, targetId);
      if (found) return found;
    }
  }
  return '';
};

// Helper function to check if a node has children
const hasChildren = (node: IndustryNode): boolean => {
  return Boolean(node.children && Object.keys(node.children).length > 0);
};

// Watch for external changes to modelValue
watch(
  () => props.modelValue,
  newValue => {
    if (newValue !== selectedIndustryId.value) {
      selectedIndustryId.value = newValue || '';
      selectedIndustryName.value = newValue
        ? findIndustryNameById(props.industries, newValue)
        : '';
    }
  },
  { immediate: true }
);

// Watch for changes to industries data (when it loads)
watch(
  () => props.industries,
  () => {
    // Update the selected industry name when industries data becomes available
    if (selectedIndustryId.value) {
      selectedIndustryName.value = findIndustryNameById(
        props.industries,
        selectedIndustryId.value
      );
    }
  },
  { immediate: true }
);

// Handle escape key to close dropdown
const handleEscape = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && isOpen.value) {
    closeDropdown();
  }
};

// Lifecycle
onMounted(() => {
  document.addEventListener('keydown', handleEscape);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscape);
});
</script>

<style scoped>
.hierarchical-industry-select {
  position: relative;
}

.dropdown-content {
  max-height: 24rem; /* 384px */
}

.industry-node {
  transition: background-color 0.15s ease-in-out;
}

.expand-icon {
  transition: transform 0.15s ease-in-out;
}

.expand-icon:hover {
  transform: scale(1.1);
}

/* Custom scrollbar for dropdown */
.dropdown-content::-webkit-scrollbar {
  width: 6px;
}

.dropdown-content::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.dropdown-content::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.dropdown-content::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Dark mode scrollbar */
.dark .dropdown-content::-webkit-scrollbar-track {
  background: #374151;
}

.dark .dropdown-content::-webkit-scrollbar-thumb {
  background: #6b7280;
}

.dark .dropdown-content::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
</style>
